<template>
  <div class="request-form">
    <div v-if="submittedEmail" class="request-form__success">
      <span class="request-form__success-icon"><CheckLineIcon :size="28" /></span>
      <h3 class="request-form__success-title" v-html="formText('success-title')"></h3>
      <p class="request-form__success-text">
        {{ successText || formText('success-text') }} <b>{{ submittedEmail }}</b>. <span v-html="formText('success-hint')"></span>
      </p>
    </div>

    <form v-else class="request-form__form" novalidate @submit.prevent="handleSubmit">
      <h2 v-if="title" class="request-form__title">{{ title }}</h2>

      <div
        v-if="showDocumentPicker"
        :class="['request-form__group', { 'request-form__group--has-error': documentsErrors.length > 0 }]"
      >
        <span class="request-form__group-label" v-html="formText('documents-label')"></span>
        <div class="request-form__chips">
          <button
            v-for="document in pickerDocuments"
            :key="document.slug"
            type="button"
            :class="['request-form__chip', { 'request-form__chip--active': isDocumentSelected(document.slug) }]"
            :aria-pressed="isDocumentSelected(document.slug) ? 'true' : 'false'"
            @click="onToggleDocument(document.slug)"
          >
            {{ isDocumentSelected(document.slug) ? '✓' : '+' }} {{ documentPickLabel(document) }}
          </button>
        </div>
        <span v-if="documentsErrors.length > 0" class="request-form__error">{{ documentsErrors[0] }}</span>
      </div>

      <LabeledTextInput
        class="request-form__input"
        :label="formText('name-label')"
        :placeholder="formText('name-placeholder')"
        :limit="255"
        v-model="form.name"
        :error-messages="fieldErrors('form.name', 'name', {
          required: formText('name-required'),
        })"
        @input="clearServerError('name')"
        @blur="touchField('form.name')"
      />
      <LabeledTextInput
        class="request-form__input"
        :label="formText('email-label')"
        :placeholder="formText('email-placeholder')"
        :limit="255"
        v-model="form.email"
        :error-messages="fieldErrors('form.email', 'email', {
          required: formText('email-required'),
          validEmail: formText('email-invalid'),
        })"
        @input="clearServerError('email')"
        @blur="touchField('form.email')"
      />
      <PhoneInput
        class="request-form__input"
        v-model="form.telephone"
        :error-message="fieldErrors('form.telephone', 'telephone', {
          validPhone: formText('phone-invalid'),
        })[0] || ''"
        @input="clearServerError('telephone')"
        @blur="touchField('form.telephone')"
      />

      <div :class="['request-form__group', { 'request-form__group--has-error': personaErrors.length > 0 }]">
        <span class="request-form__group-label" v-html="formText('persona-label')"></span>
        <div class="request-form__chips">
          <button
            v-for="item in personas"
            :key="item"
            type="button"
            :class="['request-form__chip', { 'request-form__chip--active': persona === item }]"
            :aria-pressed="persona === item ? 'true' : 'false'"
            @click="onSetPersona(item)"
          >
            {{ personaLabel(item) }}
          </button>
        </div>
        <span v-if="personaErrors.length > 0" class="request-form__error">{{ personaErrors[0] }}</span>
      </div>

      <div :class="['request-form__group', { 'request-form__group--has-error': consentErrors.length > 0 }]">
        <label class="request-form__consent">
          <input v-model="form.consent" type="checkbox" class="request-form__consent-input" @change="onConsentChange" />
          <span class="request-form__consent-box" aria-hidden="true">
            <CheckLineIcon v-if="form.consent" :size="14" color="#FFFFFF" />
          </span>
          <span>
            {{ formText('consent') }}
            <a v-if="privacyUrl" :href="privacyUrl" target="_blank" rel="noopener">{{ formText('consent-link') }}</a>
            <template v-else>{{ formText('consent-link') }}</template>
          </span>
        </label>
        <span v-if="consentErrors.length > 0" class="request-form__error">{{ consentErrors[0] }}</span>
      </div>

      <div :class="['request-form__group', { 'request-form__group--has-error': cfTokenErrors.length > 0 }]">
        <TurnstileWidget
          ref="turnstile"
          @token-received="onTurnstileToken"
          @token-expired="cfToken = null"
        />
        <span v-if="cfTokenErrors.length > 0" class="request-form__error">{{ cfTokenErrors[0] }}</span>
      </div>

      <span v-if="serverErrors.form" class="request-form__error">{{ serverErrors.form }}</span>

      <OrangeFancyButtonWithArrowAnimation type="submit" class="request-form__submit" :disabled="isSubmitting">
        {{ submitLabel || formText('submit') }}
      </OrangeFancyButtonWithArrowAnimation>
    </form>
  </div>
</template>

<script>
import { maxLength, required, requiredIf, sameAs } from 'vuelidate/lib/validators';
import { mapActions, mapGetters } from 'vuex';
import {
  getResourceDocumentTextGroup,
  RESOURCE_PERSONAS,
  RESOURCE_REQUEST_FORM_TEXT_GROUP,
  RESOURCES_HUB_TEXT_PAGE,
} from '../../config';
import { PUBLISHED_RESOURCE_DOCUMENTS } from '../../lib';
import { textBlock } from '@/entities/text-block';
import { ScrollToFirstError, ValidateForm } from '@/shared/lib/form';
import { PolicyholderValidation } from '@/shared/lib/insurance';
import { LabeledTextInput, OrangeFancyButtonWithArrowAnimation, PhoneInput } from '@/shared/ui/form';
import { CheckLineIcon } from '@/shared/ui/icons';
import TurnstileWidget from '@/shared/ui/turnstile/TurnstileWidget';

const { validUkrainianPhoneNumber, validEmail } = PolicyholderValidation();
const { scrollToFirstError } = ScrollToFirstError();

const SERVER_ERROR_FIELDS = ['name', 'email', 'telephone', 'documents', 'persona', 'consent', 'cf_token'];

export default {
  props: {
    title: {
      type: String,
      default: '',
    },
    submitLabel: {
      type: String,
      default: '',
    },
    successText: {
      type: String,
      default: '',
    },
    showDocumentPicker: {
      type: Boolean,
      default: false,
    },
    firstDocument: {
      type: String,
      default: null,
    },
  },
  mixins: [ textBlock, ValidateForm ],
  components: { LabeledTextInput, OrangeFancyButtonWithArrowAnimation, PhoneInput, TurnstileWidget, CheckLineIcon },
  data() {
    return {
      form: {
        name: '',
        email: '',
        telephone: '',
        consent: true,
      },
      cfToken: null,
      serverErrors: {},
      personas: RESOURCE_PERSONAS,
    };
  },
  validations() {
    return {
      form: {
        name: {
          required: (value) => !!(value || '').trim(),
          maxLength: maxLength(255),
        },
        email: {
          required,
          validEmail: (value) => validEmail((value || '').trim()),
          maxLength: maxLength(255),
        },
        telephone: {
          validPhone: validUkrainianPhoneNumber,
        },
        consent: {
          accepted: sameAs(() => true),
        },
      },
      selectedDocuments: {
        required: requiredIf(() => this.showDocumentPicker),
      },
      cfToken: {
        required,
      },
    };
  },
  computed: {
    ...mapGetters('agentResourcesModule', ['selectedDocuments', 'isDocumentSelected', 'persona', 'isSubmitting', 'submittedEmail']),
    pickerDocuments() {
      return [...PUBLISHED_RESOURCE_DOCUMENTS].sort((a, b) => {
        return Number(b.slug === this.firstDocument) - Number(a.slug === this.firstDocument);
      });
    },
    privacyUrl() {
      const link = this.getListBlock('promo', 'footer-links')
        .find((item) => (item.text || '').toLowerCase().includes('конфіденційн'));

      return link?.url || null;
    },
    documentsErrors() {
      return this.fieldErrors('selectedDocuments', 'documents', {
        required: this.formText('documents-required'),
      });
    },
    personaErrors() {
      return this.fieldErrors(null, 'persona');
    },
    consentErrors() {
      return this.fieldErrors('form.consent', 'consent', {
        accepted: this.formText('consent-required'),
      });
    },
    cfTokenErrors() {
      return this.fieldErrors('cfToken', 'cf_token', {
        required: this.formText('captcha-required'),
      });
    },
  },
  methods: {
    ...mapActions('agentResourcesModule', ['toggleDocument', 'setPersona', 'submitResourceRequest']),
    formText(key) {
      return this.getKeyValueListBlockValue(RESOURCES_HUB_TEXT_PAGE, RESOURCE_REQUEST_FORM_TEXT_GROUP, key);
    },
    personaLabel(persona) {
      return this.getKeyValueListBlockValue(RESOURCES_HUB_TEXT_PAGE, `${RESOURCE_REQUEST_FORM_TEXT_GROUP}-personas`, persona);
    },
    documentPickLabel(document) {
      return this.getKeyValueListBlockValue(RESOURCES_HUB_TEXT_PAGE, getResourceDocumentTextGroup(document.id), 'pick');
    },
    fieldErrors(validationPath, serverField, messages = {}) {
      const clientErrors = validationPath ? this.getValidationErrors(validationPath, messages) : [];
      const serverError = this.serverErrors[serverField];

      return serverError ? [...clientErrors, serverError] : clientErrors;
    },
    clearServerError(field) {
      if (this.serverErrors[field]) {
        this.$delete(this.serverErrors, field);
      }
    },
    onToggleDocument(slug) {
      this.toggleDocument(slug);
      this.touchField('selectedDocuments');
      this.clearServerError('documents');
    },
    onSetPersona(persona) {
      this.setPersona(persona);
      this.clearServerError('persona');
    },
    onConsentChange() {
      this.touchField('form.consent');
      this.clearServerError('consent');
    },
    onTurnstileToken(token) {
      this.cfToken = token;
      this.clearServerError('cf_token');
    },
    applyServerErrors(errors) {
      const serverErrors = {};

      Object.keys(errors).forEach((key) => {
        const field = key.startsWith('documents') ? 'documents' : key;
        const target = SERVER_ERROR_FIELDS.includes(field) ? field : 'form';

        if (!serverErrors[target]) {
          serverErrors[target] = [].concat(errors[key])[0];
        }
      });

      this.serverErrors = serverErrors;
    },
    async handleSubmit() {
      if (this.isSubmitting) {
        return;
      }

      this.serverErrors = {};

      if (!this.validateAll()) {
        await scrollToFirstError(this.$el);
        return;
      }

      const { errors } = await this.submitResourceRequest({
        name: this.form.name.trim(),
        email: this.form.email.trim(),
        telephone: this.form.telephone,
        consent: this.form.consent,
        cfToken: this.cfToken,
        page: this.$route.path,
      });

      if (errors) {
        this.applyServerErrors(errors);
        this.cfToken = null;
        this.resetFieldValidation('cfToken');
        this.$refs.turnstile?.reset();
        await scrollToFirstError(this.$el);
        return;
      }

      this.$emit('submitted');
    },
  },
};
</script>

<style lang="scss" scoped>
.request-form {
  font-family: 'Geist', sans-serif;
  color: #373A3D;

  &__form {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__title {
    margin: 0;
    font-family: 'IBM Plex Sans', sans-serif;
    font-weight: 500;
    font-size: 26px;
    line-height: 32px;
    color: #373A3D;
  }

  &__group {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__group-label {
    font-size: 16px;
    line-height: 20px;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  &__chip {
    appearance: none;
    border: 0;
    cursor: pointer;
    min-height: 44px;
    padding: 10px 16px;
    border-radius: 16px;
    font-family: 'Geist', sans-serif;
    font-size: 15px;
    background: #EBF2FF;
    color: #373A3D;

    &--active {
      background: #4555CC;
      color: #FFFFFF;
    }

    &:focus-visible {
      outline: 2px solid #6171E4;
      outline-offset: 2px;
    }
  }

  &__input {
    ::v-deep .input-label__text {
      font-family: 'Geist', sans-serif;
      font-size: 16px;
      color: #373A3D;
    }

    ::v-deep .input-label {
      font-family: 'Geist', sans-serif;
      font-size: 16px;
      color: #373A3D;
      margin-bottom: 10px;
    }

    ::v-deep .input-text {
      width: 100%;
      height: 60px;
      border: 0;
      border-radius: 16px;
      background: #EBF2FF;
      padding: 0 22px;
      font-family: 'Geist', sans-serif;
      font-size: 19px;
      color: #373A3D;
      margin-bottom: 0;

      &::placeholder {
        color: #7D8BA3;
      }
    }

    ::v-deep .input-text--has-error {
      box-shadow: inset 0 0 0 2px #FF3B30;
    }

    ::v-deep .phone-input__error {
      margin-top: 6px;
    }
  }

  &__consent {
    position: relative;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;

    a {
      color: #4555CC;
    }
  }

  &__consent-input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    pointer-events: none;

    &:focus-visible + .request-form__consent-box {
      outline: 2px solid #6171E4;
      outline-offset: 2px;
    }

    &:checked + .request-form__consent-box {
      background: #6171E4;
      box-shadow: none;
    }
  }

  &__consent-box {
    flex: none;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    box-shadow: inset 0 0 0 2px #CBDDFA;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__group &__error {
    margin-top: 0;
  }

  &__error {
    display: block;
    margin-top: -12px;
    font-size: 12px;
    color: #FF3B30;
  }

  &__submit {
    width: 100%;
  }

  &__success {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  &__success-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 16px;
    background: #FFFFFF;
    box-shadow: 0 8px 32px rgba(97, 113, 228, 0.08);
  }

  &__success-title {
    margin: 0;
    font-family: 'IBM Plex Sans', sans-serif;
    font-weight: 500;
    font-size: 26px;
    line-height: 32px;
  }

  &__success-text {
    margin: 0;
    font-size: 16px;
    line-height: 24px;
    color: #494E53;
  }
}
</style>
